import { Modal } from '@/components/common/dialog';
import { ModalButton } from '@/components/common/modal-button';
import { useConfigStore } from '@/stores';
import { generateFloorName } from '@/utils/floor-guide';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
    isOpen?: boolean;
    category?: string;
    building?: string;
    floorName?: string;
    onGoToFloor?: () => void;
    onClose?: () => void;
};

export const NoCategoryPoiPopup = ({ category, building, floorName, isOpen = false, onGoToFloor, onClose }: Props) => {
    const { t } = useTranslation();
    const buildings = useConfigStore((state) => state.config.server?.store?.buildings);

    const isShowBuildingName = useMemo(() => {
        return Array.isArray(buildings) && buildings.length > 1;
    }, [buildings]);

    const generateArialLabel = useMemo(() => {
        return `${category}, ${t('common.tts.location')}, ${t('floor_guide.text_to_speech.floor', { floorName })}, ${isShowBuildingName ? `${building}, ` : ''}`;
    }, [category, isShowBuildingName, building, floorName, t]);

    const contentAriaLabel = useMemo(() => {
        return `${t('floor_guide.message.go_to_the_nearest_floor_where_the_facilities_is_located')}, ${generateArialLabel}`;
    }, [t, generateArialLabel]);

    return (
        <Modal
            isOpen={isOpen}
            onClose={() => onClose?.()}
            className="max-w-[calc(620*var(--vw-unit))] pt-[calc(40*var(--vh-unit))] pb-[calc(40*var(--vh-unit))]"
            closeRegionOrder={4}
            closeTabIndex={5}
        >
            <section className="text-text-main01 text-center">
                <p
                    data-modal-region
                    className="focus-border mx-[calc(10*var(--vw-unit))] text-[calc(32*var(--vh-unit))] font-bold pb-[calc(40*var(--vh-unit))]"
                    tabIndex={0}
                    data-interactive
                    aria-label={t('floor_guide.title.location_of_convenience_facilities')}
                >
                    {t('floor_guide.title.location_of_convenience_facilities')}
                </p>
                <section
                    className="text-center"
                    data-modal-region
                    data-modal-region-order={1}
                    tabIndex={-1}
                    aria-label={contentAriaLabel}
                >
                    <p
                        className="focus-border mx-[calc(10*var(--vw-unit))] text-[calc(22*var(--vh-unit))] leading-[calc(30*var(--vh-unit))] font-medium whitespace-pre-wrap"
                        tabIndex={1}
                        data-interactive
                        aria-label={t('floor_guide.message.go_to_the_nearest_floor_where_the_facilities_is_located')}
                    >
                        {t('floor_guide.message.go_to_the_nearest_floor_where_the_facilities_is_located')}
                    </p>
                    <div
                        className="focus-border bg-not-contrast-bg-gray-f4 contrast:bg-bg-surface-secondary mx-auto mt-[calc(24*var(--vh-unit))] flex w-[calc(500*var(--vw-unit))] flex-col items-center gap-[calc(8*var(--vh-unit))] rounded-md py-[calc(24*var(--vh-unit))]"
                        tabIndex={2}
                        data-interactive
                        aria-label={generateArialLabel}
                    >
                        <p className="text-not-contrast-dark-393734 contrast:text-white text-[calc(28*var(--vh-unit))] font-bold">{category}</p>
                        <div className="flex items-center justify-center gap-[calc(8*var(--vh-unit))]">
                            <div className="flex items-center justify-center gap-[calc(8*var(--vw-unit))] text-[calc(20*var(--vh-unit))] leading-[calc(30*var(--vh-unit))]">
                                <p className="text-not-contrast-dark-393734 contrast:text-white number font-bold">{floorName}</p>
                                {isShowBuildingName && (
                                    <>
                                        <span className="text-not-contrast-gray-67615d contrast:text-white font-normal" aria-hidden="true">
                                            •
                                        </span>
                                        <p className="text-not-contrast-gray-67615d contrast:text-white font-normal">{building}</p>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </section>
            </section>
            <section className="mt-[calc(64*var(--vh-unit))] flex items-center justify-center gap-[calc(12*var(--vw-unit))]">
                <ModalButton
                    tabIndex={3}
                    data-interactive
                    data-modal-region
                    data-modal-region-order={2}
                    onClick={() => onClose?.()}
                    aria-label={t('floor_guide.button.cancel')}
                    type="button"
                    data-role={t('common.data_role.button')}
                    variant="secondary"
                >
                    {t('floor_guide.button.cancel')}
                </ModalButton>
                <ModalButton
                    tabIndex={4}
                    data-interactive
                    data-modal-region
                    data-modal-region-order={3}
                    onClick={() => onGoToFloor?.()}
                    aria-label={t('floor_guide.button.check')}
                    type="button"
                    data-role={t('common.data_role.button')}
                >
                    {t('floor_guide.button.check')}
                </ModalButton>
            </section>
        </Modal>
    );
};
